/**
 * The language switch, in the header on every signed-in page.
 *
 * The options are labelled in their OWN language and are never translated.
 * That is the whole point of the control: someone who has landed in a language
 * they cannot read has to be able to find the way out, and "英语 / 中文" is
 * unreadable to exactly the person who needs it most. So: "English" and "中文",
 * always, in both languages — only the group's own label moves.
 *
 * Two buttons rather than a `<select>`: with two options a select costs a menu
 * to answer a question the labels already answer, and `aria-pressed` states the
 * current language to a screen reader without opening anything.
 */
import { useI18n } from './runtime'
import type { Lang } from './runtime'

/** `lang` on each button so a screen reader pronounces 中文 as Chinese even
 *  while the surrounding page is English. */
const OPTIONS: { code: Lang; label: string; tag: string }[] = [
  { code: 'en', label: 'English', tag: 'en' },
  { code: 'zh', label: '中文', tag: 'zh-CN' },
]

export function LanguageSwitcher() {
  const { lang, setLang, t } = useI18n()

  return (
    <div
      role="group"
      aria-label={t('nav.language.label')}
      className="flex items-center gap-0.5 rounded border border-border p-0.5"
    >
      {OPTIONS.map((option) => (
        <button
          key={option.code}
          type="button"
          lang={option.tag}
          aria-pressed={lang === option.code}
          onClick={() => setLang(option.code)}
          className={`rounded px-1.5 py-0.5 transition-colors ${
            lang === option.code ? 'bg-border/60 text-fg' : 'text-muted hover:text-fg'
          }`}
        >
          {option.label}
        </button>
      ))}
    </div>
  )
}
